import { useCallback, useEffect, useState } from "react";
import { PatientTable, SearchPatientInput } from "../components/PatientSearch";
import { useAlertContext, useUserContext } from "../contexts";
import { BuildUrl, formatCentreName } from "../helpers";
import { AlertType, BannedPatient, Clinic, Patient } from "../models";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { useLocation, useNavigate } from "react-router";
import { apiAccessRequest, ROUTES } from "../constants";
import { useMsal } from "@azure/msal-react";
import CircularProgress from "@mui/material/CircularProgress";

export enum FromPage {
	BannedPatient = "BannedPatient"
}
export const PatientSearch = () => {
	const [clinic, setClinic] = useState<Clinic | undefined>(undefined);
	const [fromPage, setFromPage] = useState<FromPage | undefined>(undefined);
	const { state } = useLocation();
	const navigate = useNavigate();
	const { showAlert } = useAlertContext();
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [patients, setPatients] = useState<Patient[]>([]);
	const [searchCompleted, setSearchCompleted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		console.info("state", state);
		if (state?.clinic) {
			setClinic(state?.clinic);
		}
		if (state?.fromPage) {
			setFromPage(state?.fromPage);
		}
	}, [clinic, state]);

	const getPatientSearch = useCallback(
		async (searchPatientQueryString: string) => {
			try {
				setIsLoading(true);
				const requestUrl = BuildUrl(`Patient?${searchPatientQueryString}`);
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					...(tokenResponse && {
						Authorization: `Bearer ${tokenResponse.accessToken}`
					}),
					...{ "Content-Type": "application/json" }
				};
				const response = await fetch(requestUrl, {
					method: "GET",
					headers
				});

				if (!response.ok) {
					throw new Error("Network response was not ok");
				}

				const contentLength = response.headers.get("Content-Length");
				if (response.status !== 204 && contentLength !== "0") {
					const result = await response.json();
					const patients = result.value.map((patient: Patient) => {
						return {
							...patient,
							dateOfBirth: patient.dateOfBirth
								? format(
										parse(patient.dateOfBirth, "yyyy-MM-dd", new Date()),
										"dd/MM/yyyy"
									)
								: "",
							clinicName: formatCentreName(patient.clinicName)
						};
					});
					setSearchCompleted(true);
					setPatients(patients);
				}
			} catch (error) {
				console.info(error);
				showAlert({
					message: "Patients could not be retrieved",
					alertType: AlertType.error
				});
				return;
			} finally {
				setIsLoading(false);
			}
		},
		[account, instance, showAlert]
	);

	const getSearchPatientQueryString = useCallback(
		(clinicId: number, searchValue?: string): string => {
			if (searchValue == null || searchValue === "") {
				setPatients([]);
				setSearchCompleted(false);
				return "";
			}

			let nameQuerystring = "";
			const clinicQueryString = `&ClinicId=${clinicId}`;
			const parts = searchValue
				.split(",")
				.map((part) => part.trim())
				.filter((part) => part.length > 0);
			if (parts.length >= 1) {
				nameQuerystring = `Surname=${encodeURIComponent(parts[0])}`;
			}
			if (parts.length > 1) {
				nameQuerystring += `&FirstName=${encodeURIComponent(parts[1])}`;
			}
			return `${nameQuerystring}${clinicQueryString}`;
		},
		[]
	);

	const handleSearchInput = useCallback(
		async (value: string) => {
			const searchPatientQueryString = getSearchPatientQueryString(
				clinic?.id ?? 0,
				value.trim()
			);
			if (searchPatientQueryString !== "") {
				await getPatientSearch(searchPatientQueryString);
			}
		},
		[clinic?.id, getPatientSearch, getSearchPatientQueryString]
	);

	const handleCloseSearch = useCallback((updated: boolean) => {
		console.info(updated);
	}, []);

	const canSelectPatient = useCallback(
		async (patient: Patient): Promise<boolean> => {
			if (fromPage !== FromPage.BannedPatient) return true;

			try {
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const response = await fetch(
					BuildUrl(`Patient/BannedPatient/clinic/${patient.clinicId}`),
					{
						method: "GET",
						headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
					}
				);
				if (!response.ok) {
					throw new Error("Banned patients could not be retrieved");
				}

				const bannedPatients: BannedPatient[] =
					response.status === 204 ||
					response.headers.get("Content-Length") === "0"
						? []
						: await response.json();
				if (bannedPatients.some((banned) => banned.patientId === patient.id)) {
					showAlert({
						message: `${patient.firstName} ${patient.surname} is already banned`,
						alertType: AlertType.error
					});
					return false;
				}
				return true;
			} catch (error) {
				showAlert({
					message:
						"The patient's banned status could not be checked. Please try again.",
					alertType: AlertType.error
				});
				return false;
			}
		},
		[account, fromPage, instance, showAlert]
	);

	const goback = useCallback(() => {
		console.info("goback", fromPage);
		if (fromPage === FromPage.BannedPatient) {
			navigate(ROUTES.centreOperations, {
				state: { tabOption: 3, clinic }
			});
		}
	}, [clinic, fromPage, navigate]);

	return (
		<>
			<div className="flex flex-col p-4 gap-4 ch-lightestgrey-background">
				<div
					className="flex"
					style={{ cursor: "pointer" }}
					onClick={() => goback()}
				>
					<img
						src="images/svg/arrow-left-black.svg"
						className="mr-2"
						height={22}
						width={22}
						alt="back to banned patients"
					/>
					<div>Banned Patients</div>
				</div>
				<div className="flex">
					<div className="ch-l-text">Add Banned Patient</div>
				</div>
				<div className="flex flex-col items-center w-full">
					<div className="flex justify-start w-full gap-8">
						<div className="" style={{ width: 600 }}>
							<SearchPatientInput setSearchValue={handleSearchInput} />
						</div>
						<div className="ch-m-text mt-2">Centre: {clinic?.name}</div>
					</div>
					<div className="w-full mt-4">
						{isLoading ? (
							<div className="flex justify-center">
								<CircularProgress size="80px" />
							</div>
						) : (
							<PatientTable
								patients={patients}
								clinic={clinic}
								fromPage={fromPage}
								searchCompleted={searchCompleted}
								closeSearch={handleCloseSearch}
								canSelectPatient={canSelectPatient}
							/>
						)}
					</div>
				</div>
			</div>
		</>
	);
};
